/**
 * src/components/shared/ViewContainer.jsx — the standard frame for every routed view.
 *
 * TICKET-UI-BUTTON-SYSTEM. The shared <main> (NavigationShell) carries no edge
 * padding, so historically each view hardcoded its own (padding:16, '24px 16px',
 * p-4, varying maxWidths) and they drifted. This is the ONE place that defines a
 * view's outer frame: edge padding from the central --page-pad token, a centered
 * max-width column, border-box + overflow-x guard.
 *
 * New tab? Wrap its content in <ViewContainer> — never hardcode padding on a view
 * root. Adjust spacing app-wide via --page-pad (src/styles.js); adjust the column
 * width here or per-view via the maxWidth prop. See src/views/CLAUDE.md.
 *
 * Props:
 *   maxWidth  number|null  content column cap in px (default 1280). null = full-bleed.
 *   center    bool         center the column when capped (default true).
 *   testId    string       data-testid passthrough (keep the view's existing id).
 *   className/style         escape hatches; style is spread LAST (use sparingly).
 */
export default function ViewContainer({
  children,
  maxWidth = 1280,
  wide = false,
  center = true,
  testId,
  className,
  style,
  ...rest
}) {
  return (
    <div
      data-testid={testId}
      className={className}
      style={{
        width: '100%',
        // wide: a work view (board, dashboard) keeps maxWidth below 1600px and drops
        // it above (--view-wide-max, src/styles.js) — TICKET-1165 ultrawide screens.
        maxWidth: maxWidth ? (wide ? `var(--view-wide-max, ${maxWidth}px)` : `${maxWidth}px`) : undefined,
        margin: center && maxWidth ? '0 auto' : undefined,
        padding: 'var(--page-pad)',
        boxSizing: 'border-box',
        overflowX: 'hidden',
        ...style,
      }}
      {...rest}
    >
      {children}
    </div>
  );
}
